import { useEffect, useRef, useState, type Ref } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronDown, Heart, MessageCircle, MoreHorizontal, Music2, Pause, Repeat, Share2, Shuffle, SkipBack, SkipForward, ThumbsDown, ThumbsUp, Volume2, VolumeX,
} from 'lucide-react';
import type { CoverInfo } from '@/lib/quicktools-api';
import { trackEvent } from '@/lib/analytics';
import { LivingCover, type LivingCoverHandle } from './LivingCover';
import { ExampleReel } from './CoverExamples';
import { cn } from '@/lib/utils';

// THE SAME SONG ON A PHONE, WITHOUT AND WITH A LIVING COVER (owner 2026-10-19: "create a normal spotify song playing
// example compared to a spotify song playing with ours that looks just like a spotify song playing on their phone and
// we could do the same for soundcloud or youtube"). Drawn by us in each app's colours and layout (no app logos), with
// one of the owner's examples: a still on the left, the living one on the right.

type App = 'spotify' | 'youtube' | 'soundcloud';
const APPS: { id: App; name: string; color: string }[] = [
  { id: 'spotify', name: 'Spotify', color: '#1DB954' },
  { id: 'youtube', name: 'YouTube', color: '#FF0033' },
  { id: 'soundcloud', name: 'SoundCloud', color: '#FF5500' },
];

function Media({ example, living, coverRef }: { example: CoverInfo; living: boolean; coverRef?: Ref<LivingCoverHandle> }) {
  // A cover wider than the 9:16 screens (his 4:5 police car, owner 2026-11-01): its own box as tall as the screen, centred,
  // so its overlays stay on what they were placed on.
  const shaped = example.size && Math.abs(example.size.w / example.size.h - 9 / 16) >= 0.01 ? example.size : null;
  if (shaped) {
    const box = { height: '100%', aspectRatio: `${shaped.w} / ${shaped.h}` };
    return (
      <div className="absolute left-1/2 top-0 -translate-x-1/2" style={box}>
        {living ? (
          <LivingCover ref={coverRef} video={example.video_url} poster={example.poster_url} kinds={example.float || []} amount={example.amount} opacity={example.opacity} spot={example.spot} beats={example.beats} shakes={example.shakes} dark={example.dark} frame={shaped} label={example.title || ''} className="absolute inset-0 h-full w-full" />
        ) : (
          <img src={example.before_url || example.poster_url} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
        )}
      </div>
    );
  }
  return living ? (
    <LivingCover ref={coverRef} video={example.video_url} poster={example.poster_url} kinds={example.float || []} amount={example.amount} opacity={example.opacity} spot={example.spot} beats={example.beats} shakes={example.shakes} dark={example.dark} label={example.title || ''} className="absolute inset-0 h-full w-full" />
  ) : (
    <img src={example.before_url || example.poster_url} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
  );
}

function Screen({ app, example, living, song, artist, coverRef }: { app: App; example: CoverInfo; living: boolean; song: string; artist: string; coverRef?: Ref<LivingCoverHandle> }) {
  const icon = 'h-[18px] w-[18px]';
  if (app === 'youtube') {
    return (
      <>
        <Media example={example} living={living} coverRef={coverRef} />
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 to-transparent" />
        <div className="absolute right-2 bottom-24 flex flex-col items-center gap-3.5 text-white">
          {[ThumbsUp, ThumbsDown, MessageCircle, Share2].map((I, i) => (
            <span key={i} className="flex flex-col items-center gap-0.5"><I className={icon} /><span className="text-[8px]">{['42K', '', '1.2K', ''][i] || '·'}</span></span>
          ))}
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white/15 ring-2 ring-white/80"><Music2 className="h-4 w-4" /></span>
        </div>
        <div className="absolute inset-x-3 bottom-4 text-white">
          <div className="flex items-center gap-1.5">
            <span className="h-6 w-6 overflow-hidden rounded-full bg-white/20"><img src={example.poster_url} alt="" className="h-full w-full object-cover" /></span>
            <span className="text-[11px] font-semibold">@{artist.replace(/\s+/g, '').toLowerCase()}</span>
            <span className="rounded-full bg-[#FF0033] px-2 py-0.5 text-[9px] font-bold">Subscribe</span>
          </div>
          <p className="mt-1.5 line-clamp-1 text-[11px]">{song} · out now</p>
          <p className="mt-1 flex items-center gap-1 text-[10px] text-white/85"><Music2 className="h-3 w-3" /> {song} · {artist}</p>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-[3px] bg-white/25"><i className="block h-full w-2/5 bg-[#FF0033]" /></div>
      </>
    );
  }
  if (app === 'soundcloud') {
    return (
      <div className="absolute inset-0 bg-[#121212]">
        <div className="absolute inset-x-0 top-0 h-[62%] overflow-hidden"><Media example={example} living={living} coverRef={coverRef} /></div>
        <div className="absolute inset-x-0 top-[44%] h-[18%] bg-gradient-to-t from-[#121212] to-transparent" />
        <div className="absolute inset-x-3 top-7 flex items-center justify-between text-white"><ChevronDown className={icon} /><MoreHorizontal className={icon} /></div>
        <div className="absolute inset-x-3 top-[64%] text-white">
          <p className="line-clamp-1 text-[13px] font-bold">{song}</p>
          <p className="text-[10.5px] text-white/60">{artist}</p>
          <div className="mt-2.5 flex h-8 items-end gap-[2px]">
            {Array.from({ length: 46 }, (_, i) => (
              <i key={i} className="block w-[2px] rounded-full" style={{ height: `${25 + 70 * Math.abs(Math.sin(i * 0.7) * Math.cos(i * 0.23))}%`, background: i < 18 ? '#FF5500' : 'rgba(255,255,255,0.35)' }} />
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between px-2">
            <Heart className={icon} /><SkipBack className={icon} />
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#FF5500]"><Pause className="h-5 w-5 fill-white" /></span>
            <SkipForward className={icon} /><Repeat className={icon} />
          </div>
        </div>
      </div>
    );
  }
  // Spotify-style: the Canvas fills the screen behind the song.
  return (
    <>
      <Media example={example} living={living} coverRef={coverRef} />
      <div className="absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-black/60 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
      <div className="absolute inset-x-3 top-7 flex items-center justify-between text-white">
        <ChevronDown className={icon} />
        <span className="text-center text-[8.5px] leading-tight"><span className="block text-white/70">PLAYING FROM ARTIST</span><span className="font-bold">{artist}</span></span>
        <MoreHorizontal className={icon} />
      </div>
      <div className="absolute inset-x-3 bottom-3 text-white">
        <div className="flex items-end justify-between">
          <div className="min-w-0"><p className="line-clamp-1 text-[14px] font-bold">{song}</p><p className="text-[10.5px] text-white/70">{artist}</p></div>
          <Heart className={cn(icon, 'fill-[#1DB954] text-[#1DB954]')} />
        </div>
        <div className="mt-2 h-[3px] rounded-full bg-white/30"><i className="block h-full w-2/5 rounded-full bg-white" /></div>
        <div className="mt-0.5 flex justify-between text-[8px] text-white/60"><span>1:12</span><span>3:05</span></div>
        <div className="mt-1.5 flex items-center justify-between px-1">
          <Shuffle className={cn(icon, 'text-[#1DB954]')} /><SkipBack className={cn(icon, 'fill-white')} />
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white"><Pause className="h-5 w-5 fill-black text-black" /></span>
          <SkipForward className={cn(icon, 'fill-white')} /><Repeat className={icon} />
        </div>
      </div>
    </>
  );
}

function Phone({ children, label, accent, size = 'w-[min(42vw,230px)]' }: { children: React.ReactNode; label: string; accent?: string; size?: string }) {
  return (
    <figure className="m-0 flex flex-col items-center gap-2.5">
      <div className={cn('relative aspect-[9/19.5] overflow-hidden', size, 'rounded-[2rem] border-[5px] border-[#1c1f26] bg-black shadow-[0_30px_60px_-25px_rgba(0,0,0,0.9)] ring-1 ring-white/10')}>
        <div className="absolute left-1/2 top-1.5 z-10 h-4 w-16 -translate-x-1/2 rounded-full bg-black" />
        {children}
      </div>
      <figcaption className="text-center text-[13.5px] font-bold" style={accent ? { color: accent } : undefined}>{label}</figcaption>
    </figure>
  );
}

/** One example on two phones, still and living (owner 2026-10-20: "scroll through the cell phone examples the same
 *  but with both the before and afters displayed on each phone"). Each phone is the comparison's size, 230px at most
 *  ("this size is how big those smaller phones you made should be"), and narrower only when the screen is. */
export function PhonePair({ example, app = 'spotify', onSound }: { example: CoverInfo; app?: App; onSound?: (on: boolean) => void }) {
  const { t } = useTranslation();
  const song = t('qt.compare.song');
  const artist = t('qt.compare.artist');
  const accent = (APPS.find((a) => a.id === app) || APPS[0]).color;
  // The song is off until tapped, like the other examples (owner 2026-10-20: "audio optional with a click").
  const ref = useRef<LivingCoverHandle>(null);
  const [sound, setSound] = useState(false);
  useEffect(() => () => onSound?.(false), [onSound]);
  const toggle = () => {
    const v = ref.current?.video;
    if (!v) return;
    if (sound) {
      v.muted = true;
      setSound(false);
      onSound?.(false);
      return;
    }
    // One example plays at a time.
    document.querySelectorAll<HTMLVideoElement>('video').forEach((o) => {
      if (o !== v) o.muted = true;
    });
    ref.current?.startSound();
    setSound(true);
    onSound?.(true);
    trackEvent('living_cover_example_sound', { where: 'phones' });
  };
  return (
    <div className="grid grid-cols-[repeat(2,minmax(0,230px))] justify-center gap-2 sm:gap-4" data-testid="qt-phone-pair">
      <Phone size="w-full" label={t('qt.compare.without')}><Screen app={app} example={example} living={false} song={song} artist={artist} /></Phone>
      <Phone size="w-full" label={t('qt.compare.with')} accent={accent}>
        <Screen app={app} example={example} living song={song} artist={artist} coverRef={ref} />
        {example.song && (
          <button
            type="button"
            onClick={toggle}
            aria-pressed={sound}
            aria-label={sound ? t('qt.examples.soundOff') : t('qt.examples.soundOn')}
            className="absolute right-2 top-14 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/70 text-white ring-1 ring-white/20"
            data-testid="qt-phone-sound"
          >
            {sound ? <Volume2 className="h-5 w-5" aria-hidden /> : <VolumeX className="h-5 w-5" aria-hidden />}
          </button>
        )}
      </Phone>
    </div>
  );
}

/** The same song on a phone, without and with (owner 2026-10-19), flipped through every example like the /canvas phones
 *  (owner 2026-10-20: it moves into the home page's Living Cover block in place of the one swipeable example). */
export function PhoneCompare({ examples, className }: { examples: CoverInfo[]; className?: string }) {
  const { t } = useTranslation();
  const [app, setApp] = useState<App>('spotify');
  const current = APPS.find((a) => a.id === app) || APPS[0];
  if (!examples.length) return null;
  return (
    <section className={cn('mx-auto w-full max-w-3xl text-center', className)} data-testid="phone-compare" aria-labelledby="phone-compare-title">
      <h2 id="phone-compare-title" className="text-balance font-display text-[1.6rem] font-bold leading-tight sm:text-[2.1rem]">{t('qt.compare.title')}</h2>
      <p className="mx-auto mt-2 max-w-xl text-[15px] text-muted-foreground">{t('qt.compare.lead', { app: current.name })}</p>
      <div className="mt-5 inline-flex rounded-full border border-white/10 bg-white/[0.03] p-1" role="tablist" aria-label={t('qt.compare.pick')}>
        {APPS.map((a) => (
          <button
            key={a.id}
            type="button"
            role="tab"
            aria-selected={a.id === app}
            onClick={() => setApp(a.id)}
            className={cn('min-h-[40px] rounded-full px-4 text-[14px] font-bold transition-colors', a.id === app ? 'bg-white/10' : 'text-muted-foreground hover:text-foreground')}
            style={a.id === app ? { color: a.color } : undefined}
            translate="no"
          >
            {a.name}
          </button>
        ))}
      </div>
      <div className="mt-6">
        <ExampleReel examples={examples} slide={(ex, hold) => <PhonePair example={ex} app={app} onSound={hold} />} />
      </div>
      <p className="mx-auto mt-3 max-w-md text-[12.5px] text-muted-foreground">{t('qt.compare.note', { app: current.name })}</p>
    </section>
  );
}
